<script setup lang="ts">
import { ref, onMounted } from "vue";
import AgilaButton from "frontend-component-templates/src/components/agilaButton.vue";
import productsApi from "../api/products";
import { PET_TYPE, PRODUCT_GROUP } from "../store/variables";
import { Product } from "../classes/product";
import { ProductGroup } from "../classes/productGroup";
import { featureIsDiff } from "../helper/general";
import ListContentRowComponent from "../components/sections/ListContentRowComponent.vue";
import { trackObject } from "../helper/tracking";
import { useFocusVisible } from "frontend-component-templates/src/composables/useFocusVisible";

const selectedProductIdentifier = ref("TKS");
const petType = ref(PET_TYPE.DOG);
const selectedProductGroupId = ref(PRODUCT_GROUP.TKS);
const productGroup = ref<ProductGroup>();
const product = ref<Product>();
const showAllFields = ref<boolean>(false);

productsApi.setApiKeys();

onMounted(async () => {
  productGroup.value = await productsApi.getProductGroup(
    selectedProductGroupId.value,
    petType.value
  );
  product.value = productGroup.value?.products?.find(
    (product) => product.identifier === selectedProductIdentifier.value
  );
});

useFocusVisible();
checkDataAttributes();

function checkDataAttributes() {
  const quoteCalculationElement = document.getElementById("quote-calculation");
  const dataProduct = quoteCalculationElement.getAttribute("data-product");
  if (dataProduct || window.abList) {
    selectedProductIdentifier.value = dataProduct || window.abList;
    if (selectedProductIdentifier.value.indexOf("OPS") > -1) {
      selectedProductGroupId.value = PRODUCT_GROUP.OPS;
    } else if (selectedProductIdentifier.value.indexOf("HPS") > -1) {
      selectedProductGroupId.value = PRODUCT_GROUP.HPS;
    }
  }
  const dataPetType = quoteCalculationElement.getAttribute("data-pet-type");
  if (dataPetType === "dog") {
    petType.value = PET_TYPE.DOG;
  } else if (dataPetType === "cat") {
    petType.value = PET_TYPE.CAT;
  }
}

function selectProduct(): void {
  trackObject("select_item", [product.value], petType.value);

  const params = new URLSearchParams();
  params.set("pet", petType.value);
  params.set("productGroup", selectedProductGroupId.value);
  params.set("product", product.value.id);

  window.location.href =
    process.env.VUE_APP_ORIGIN + "antrag2/#/1/tier/typ?" + params.toString();
}

function navigateToAlt() {
  if (petType.value === PET_TYPE.DOG) {
    window.location.href =
      process.env.VUE_APP_ORIGIN +
      "versicherungen/hundeversicherung/hundekrankenversicherung";
  } else {
    window.location.href =
      process.env.VUE_APP_ORIGIN +
      "versicherungen/katzenversicherung/katzenkrankenversicherung";
  }
}

const noHeaderVisible = ref(false);
document.addEventListener("scroll", () => {
  const header = document
    .querySelector("#header > div")
    ?.shadowRoot?.querySelector("div > header");
  if (header) {
    const headerStyle = window.getComputedStyle(header);
    noHeaderVisible.value = headerStyle.display === "none";
  } else {
    noHeaderVisible.value = true;
  }
});
</script>

<template>
  <div class="quote-list" v-if="product">
    <div class="quote-table">
      <div
        class="quote-table-head"
        :class="{
          'no-header': noHeaderVisible,
        }"
      >
        <div class="product-headline">
          <div class="product-name">{{ product.name }}</div>
          <div class="product-price">
            ab {{ product.getDisplayPrice() }} <span>mtl.</span>
          </div>
        </div>
        <agila-button @click="selectProduct()" :text="product.name + ' wählen'">
        </agila-button>
      </div>
      <div class="quote-table-content">
        <template v-for="feature in productGroup.features" :key="feature.id">
          <list-content-row-component
            :product="product"
            :feature="feature"
            v-if="featureIsDiff(feature, productGroup)"
          ></list-content-row-component>
        </template>
        <div
          class="table-row show-all"
          tabindex="0"
          @keyup.enter="showAllFields = !showAllFields"
          @click="showAllFields = !showAllFields"
        >
          <span>Gesamte Leistungen ansehen</span>
          <span
            :class="{
              'agila-icon-down': !showAllFields,
              'agila-icon-up': showAllFields,
            }"
          ></span>
        </div>
        <template v-if="showAllFields">
          <div v-for="feature in productGroup.features" :key="feature.id">
            <list-content-row-component
              :product="product"
              :feature="feature"
              v-if="!featureIsDiff(feature, productGroup)"
            ></list-content-row-component>
          </div>
        </template>
      </div>
    </div>
    <div class="quote-alt" v-if="selectedProductGroupId === PRODUCT_GROUP.OPS">
      <div class="quote-alt-headline">
        Du willst dein Tier noch besser absichern?
      </div>
      <agila-button
        @click="navigateToAlt()"
        :text="'Zum Tierkrankenschutz'"
        :secondary="true"
      >
        <div class="agila-icon-right-sm"></div>
      </agila-button>
    </div>
  </div>
</template>

<style lang="scss">
$breakpoint-l: 996px;
$breakpoint-m: 747px;
$breakpoint-xs: 400px;

.quote-list {
  margin: 0 auto;
  max-width: 640px;

  .quote-table {
    border-radius: 8px;
    border: 1px solid var(--color-blue-200);

    .quote-table-head {
      background-color: var(--color-blue-100);
      padding: 16px;
      border-radius: 8px 8px 0 0;
      @media screen and (min-width: $breakpoint-m) {
        display: grid;
        grid-template-columns: 1fr max-content;
        align-items: center;
      }

      @media screen and (max-width: $breakpoint-m - 1px) {
        position: sticky;
        top: 102px !important;
        &.no-header {
          top: 0 !important;
        }
      }

      .product-headline {
        color: var(--color-gray-yellow-button);
        font-family: var(--font-roboto-bold), sans-serif;
        justify-content: space-between;
        align-items: center;

        @media screen and (max-width: $breakpoint-m - 1px) {
          display: flex;
        }

        .product-name {
          font-size: 18px;
          line-height: 26px;
          letter-spacing: 0.54px;
          @media screen and (max-width: $breakpoint-m - 1px) {
            width: 50%;
          }
          @media screen and (min-width: $breakpoint-m) {
            margin-bottom: 4px;
          }
        }

        .product-price {
          font-size: 24px;
          line-height: 32px;
          letter-spacing: 0.24px;

          span {
            color: var(--color-gray-yellow-button);
            font-family: var(--font-open-sans-regular), sans-serif;
            font-size: 16px;
            line-height: 24px;
          }
        }
      }

      .p-button {
        @media screen and (max-width: $breakpoint-m - 1px) {
          margin-top: 12px;
        }
        width: 100%;
        height: fit-content;
      }
    }
    .quote-table-content {
      .table-row {
        border-top: 1px solid var(--color-blue-200);
        padding: 12px;
        display: flex;
        align-items: center;

        .table-row-left {
          width: 75%;
          padding-right: 60px;
          color: var(--color-gray-yellow-button);
          font-family: var(--font-open-sans-regular), sans-serif;
          font-size: 16px;
          line-height: 150%;
        }
        .table-row-right {
          width: 25%;
          color: var(--color-gray-yellow-button);
          font-family: var(--font-roboto-bold), sans-serif;
          font-size: 18px;
          line-height: 26px;
          letter-spacing: 0.54px;

          div {
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .text-small {
          font-family: var(--font-open-sans-regular), sans-serif;
          font-size: 13px;
          line-height: 1;
          overflow: hidden;
          text-overflow: ellipsis;
          padding-bottom: 1px;
        }

        &.show-all {
          justify-content: center;
          color: var(--color-blue-standard);
          font-family: var(--font-open-sans-regular), sans-serif;
          font-size: 18px;
          line-height: 26px;
          padding: 16px 0;
          cursor: pointer;
          align-items: center;

          span:nth-of-type(1) {
            margin-right: 12px;
          }
        }
      }
    }
  }
  .quote-alt {
    margin-top: 44px;

    .quote-alt-headline {
      color: var(--color-gray-yellow-button);
      font-family: var(--font-roboto-bold), sans-serif;
      font-size: 18px;
      line-height: 26px;
      margin-bottom: 8px;
    }
  }
}
</style>
